import { Tooltip, theme } from "antd";
import { BookOpen, ShieldAlert } from "lucide-react";
import type React from "react";

// The grounding signal already rides on the `complete` frame
// (`used_knowledge_base` / `documents_found`) and used to be dropped on the
// floor. That silence is the bug: when the MCP tool layer is unreachable
// `assemble_mcp_tools` degrades to zero tools and the model answers from its
// priors, producing a bubble that looks EXACTLY like a grounded one.
//
// So this renders in BOTH states. An ungrounded answer is not hidden, it is
// labelled. Absence of the chip means the turn reported no grounding metadata
// at all (legacy turn, or still streaming) — also distinct from "ungrounded".

export interface GroundingMetadata {
	used_knowledge_base?: boolean;
	documents_found?: number;
	generation_time_ms?: number;
}

/**
 * Grounding for a TOOL-grounded surface (the mapping chat), which has no RAG and so
 * no `documents_found`. `unavailable` names the company lists that FAILED to load
 * from QuickBooks this turn (accounts / names / classes). ADR 0019, EQ-R-0019-005.
 */
export interface ListGrounding {
	grounded: boolean;
	unavailable: string[];
}

const LIST_NAMES: Record<string, string> = {
	accounts: "accounts",
	names: "vendors and customers",
	classes: "classes",
};

interface GroundingChipProps {
	metadata?: GroundingMetadata;
	/** Use instead of `metadata` on a surface that grounds in lists, not retrieval. */
	lists?: ListGrounding;
}

export const GroundingChip: React.FC<GroundingChipProps> = ({
	metadata,
	lists,
}) => {
	const { token } = theme.useToken();

	let isGrounded: boolean;
	let label: string;
	let tooltip: string;

	if (lists) {
		isGrounded = lists.grounded;
		const missing = lists.unavailable.map((k) => LIST_NAMES[k] ?? k).join(", ");
		label = isGrounded ? "Your QuickBooks lists" : "Lists unavailable";
		tooltip = isGrounded
			? "Suggestions are drawn from this company's own accounts, vendors, customers and classes."
			: `Couldn't load ${missing || "your lists"} from QuickBooks, so suggestions may not match this company's records. Verify before applying.`;
	} else {
		// No metadata reported for this turn — say nothing rather than assert either
		// state. Distinguishable from an explicit "not grounded".
		if (!metadata || metadata.used_knowledge_base === undefined) return null;

		isGrounded = metadata.used_knowledge_base === true;
		const count = metadata.documents_found ?? 0;

		label = isGrounded
			? `${count} ${count === 1 ? "source" : "sources"}`
			: "Not grounded";

		tooltip = isGrounded
			? `Answered using ${count} retrieved ${count === 1 ? "source" : "sources"} from your data.`
			: "Answered without retrieved sources. Verify before acting on any figure.";
	}

	return (
		<Tooltip title={tooltip}>
			{/*
			 * A real <button>, not a styled <span>: revealing the tooltip IS an
			 * interaction, and AntD's Tooltip opens on focus as well as hover. As a
			 * span with tabIndex it was both a lint violation (a non-interactive tab
			 * stop) and unreachable without a mouse — which mattered because for the
			 * ungrounded case the tooltip carries the "verify before acting on any
			 * figure" warning. aria-label repeats it so screen readers get it from
			 * the accessible name without needing the tooltip to open at all.
			 */}
			<button
				type="button"
				aria-label={`${label}. ${tooltip}`}
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: 4,
					lineHeight: 1.6,
					padding: "0 6px",
					borderRadius: 10,
					color: isGrounded ? token.colorSuccess : token.colorWarning,
					background: isGrounded ? token.colorSuccessBg : token.colorWarningBg,
					border: `1px solid ${
						isGrounded ? token.colorSuccessBorder : token.colorWarningBorder
					}`,
					// Reset the UA button styling the chip must not inherit.
					font: "inherit",
					fontSize: 10.5,
					margin: 0,
					cursor: "default",
				}}
			>
				{/* Decorative: the adjacent label already carries the meaning. */}
				{isGrounded ? (
					<BookOpen size={10} aria-hidden="true" />
				) : (
					<ShieldAlert size={10} aria-hidden="true" />
				)}
				{label}
			</button>
		</Tooltip>
	);
};
